import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { ThreeDots } from "react-loader-spinner";
import useThemeColors from "hooks/useThemeColors";

import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";

import alignmentSessionStyles from "./styles";
import DimensionTable from "./DimensionTable";
import KeyMessagesPanel from "./KeyMessagesPanel";
import FocusModeWrapper from "./FocusModeWrapper";

const useStyles = makeStyles(alignmentSessionStyles);

/**
 * ByDimensionSection component
 * Displays dimension breakdown tables with delta metrics.
 * Data comes from the orchestrator's dimension endpoint.
 */
function ByDimensionSection({
  clientColor: propsClientColor,
  dataOrchestrator,
  highlightEnabled,
  focusMode = false,
}) {
  const classes = useStyles();
  const { i18n } = useTranslation();
  const { clientColor: themeClientColor } = useThemeColors();
  const clientColor = propsClientColor || themeClientColor;

  // Dimension selections (keys like 'channel', 'category', 'region')
  const [dimension1, setDimension1] = useState(null);
  const [dimension2, setDimension2] = useState(null);
  const [dimension3, setDimension3] = useState(null);

  // State for key messages
  const [keyMessages, setKeyMessages] = useState([]);

  // ========================= Data from orchestrator =========================

  // The orchestrator loads data automatically — no on-demand trigger needed.
  const dimensionResponse = dataOrchestrator?.getDimensionData?.() || null;
  const isDataLoading = !dimensionResponse;

  // Available dimension keys (from backend response)
  const availableDimensions = useMemo(() => {
    if (!dimensionResponse?.dimensions) return [];
    return Object.keys(dimensionResponse.dimensions);
  }, [dimensionResponse]);

  // Translated dimension names from backend
  const dimensionNames = useMemo(() => {
    if (!dimensionResponse?.dimension_names) return {};
    const names = {};
    const backendNames = dimensionResponse.dimension_names;
    for (const [key, value] of Object.entries(backendNames)) {
      if (typeof value === "object") {
        names[key] = value[i18n.language] || value.en || key;
      } else {
        names[key] = value;
      }
    }
    return names;
  }, [dimensionResponse, i18n.language]);

  // Auto-set default dimensions when data arrives
  useEffect(() => {
    if (availableDimensions.length === 0) return;
    if (!dimension1) setDimension1(availableDimensions[0] || null);
    if (!dimension2) setDimension2(availableDimensions[1] || null);
    if (!dimension3) setDimension3(availableDimensions[2] || null);
  }, [availableDimensions, dimension1, dimension2, dimension3]);

  // Build dimensionData object in the format DimensionTable expects:
  // { [dimKey]: { name, elements: [...] }, total: { ... } }
  const dimensionData = useMemo(() => {
    if (!dimensionResponse) return {};

    const data = {};

    // Total
    if (dimensionResponse.total) {
      data.total = dimensionResponse.total;
    }

    // Each dimension
    if (dimensionResponse.dimensions) {
      for (const [key, value] of Object.entries(dimensionResponse.dimensions)) {
        data[key] = {
          ...value,
          name: dimensionNames[key] || value.name || key,
        };
      }
    }

    return data;
  }, [dimensionResponse, dimensionNames]);

  // Build dimension options for DimensionTable dropdown
  const dimensionOptions = useMemo(
    () =>
      availableDimensions.map((key) => ({
        value: key,
        label: dimensionNames[key] || key,
      })),
    [availableDimensions, dimensionNames],
  );

  // Loading spinner
  const spinner = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px",
      }}
    >
      <ThreeDots width="40px" color={clientColor} />
    </div>
  );

  const hasTotalData = dimensionData.total?.elements?.length > 0;

  return (
    <FocusModeWrapper focusMode={focusMode}>
      <div
        className={classes.byDimensionContainer}
        style={focusMode ? { height: "100%", marginTop: 0 } : {}}
      >
        {/* Card 1 - Dimension 1 */}
        <Card className={classes.byDimensionCard}>
          <CardBody className={classes.byDimensionCardBody}>
            {isDataLoading ? (
              spinner
            ) : dimension1 && dimensionData[dimension1] && hasTotalData ? (
              <DimensionTable
                dimensionKey={dimension1}
                dimensionData={dimensionData}
                totalData={dimensionData.total}
                onDimensionChange={setDimension1}
                highlightEnabled={highlightEnabled}
                dimensionOptions={dimensionOptions}
              />
            ) : (
              spinner
            )}
          </CardBody>
        </Card>

        {/* Card 2 - Dimension 2 */}
        <Card className={classes.byDimensionCard}>
          <CardBody className={classes.byDimensionCardBody}>
            {isDataLoading ? (
              spinner
            ) : dimension2 && dimensionData[dimension2] && hasTotalData ? (
              <DimensionTable
                dimensionKey={dimension2}
                dimensionData={dimensionData}
                totalData={dimensionData.total}
                onDimensionChange={setDimension2}
                highlightEnabled={highlightEnabled}
                dimensionOptions={dimensionOptions}
              />
            ) : (
              spinner
            )}
          </CardBody>
        </Card>

        {/* Card 3 - Dimension 3 */}
        <Card className={classes.byDimensionCard}>
          <CardBody className={classes.byDimensionCardBody}>
            {isDataLoading ? (
              spinner
            ) : dimension3 && dimensionData[dimension3] && hasTotalData ? (
              <DimensionTable
                dimensionKey={dimension3}
                dimensionData={dimensionData}
                totalData={dimensionData.total}
                onDimensionChange={setDimension3}
                highlightEnabled={highlightEnabled}
                dimensionOptions={dimensionOptions}
              />
            ) : (
              spinner
            )}
          </CardBody>
        </Card>

        {/* Key Messages Panel */}
        <KeyMessagesPanel
          messages={keyMessages}
          onMessagesChange={setKeyMessages}
        />
      </div>
    </FocusModeWrapper>
  );
}

ByDimensionSection.propTypes = {
  clientColor: PropTypes.string,
  dataOrchestrator: PropTypes.object,
  highlightEnabled: PropTypes.bool,
  focusMode: PropTypes.bool,
};

ByDimensionSection.defaultProps = {
  clientColor: null,
  dataOrchestrator: null,
  highlightEnabled: false,
  focusMode: false,
};

export default ByDimensionSection;
